// only add Redux-Logger on the client-side because it causes problems server-side.
import isNode from 'detect-node';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { applyMiddleware, createStore } from 'redux';
import { createSession } from 'redux-session';
import thunkMiddleware from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';
import * as Actions from './actions';
import rootReducer from './reducers';
import simplePromise from './middleware/simple-promise';
import Cookies from 'js-cookie';
import * as Sentry from '@sentry/react';
import _ from 'lodash';

// The extension keeps some of the user in sync with the web app - notably the
// source settings, which the status indicator reads. It sends its own stored
// copy of the user, which is a SUBSET: saveUserProfile (extension auth.js)
// rebuilds profile.roles as { type, status } only, dropping level.
//
// A plain _.merge welds those arrays together BY INDEX. When the extension's
// cached copy is ordered differently from the web app's - it is written at a
// different time, so it can be - index 0 of one lands on index 0 of the other
// and roles swap their fields:
//
//   web app   [ student(level 1), community(level 3), creator ]
//   extension [ community,        student                     ]
//   merged    [ community(level 1), student(level 3), creator ]
//
// Every level reader then finds the community role holding level 1
// (DailyReadingActivity.js, WeeklyGoals.js, PortfolioSidebar.js all fall back
// to 1 when level is missing or wrong), so the student's level silently drops
// until they reload. Matching on `type` instead of position keeps the settings
// sync working without letting one role's fields land on another.
//
// What the extension may legitimately change here is a wider question - see
// docs/analysis, tasks/extension-webapp-user-sync.md in averpointWebServer.
function mergeUserFromExtension(current, incoming) {
  return _.mergeWith({}, current, incoming, (currentValue, incomingValue, key) => {
    if (
      key === 'roles' &&
      Array.isArray(currentValue) &&
      Array.isArray(incomingValue)
    ) {
      return currentValue.map((role) => {
        const match = incomingValue.find((r) => r && r.type === role.type);

        return match ? { ...role, ...match } : role;
      });
    }

    // Anything else falls through to the default merge.
    return undefined;
  });
}

const { NODE_ENV } = process.env;
const isProduction = NODE_ENV === 'production';

let store;

export default (history, initialState) => {
  if (store) {
    return store;
  }

  const promiseMiddleware = simplePromise();
  const middleware = [thunkMiddleware, promiseMiddleware];

  if (!isNode) {
    // middleware.push(createLogger());
    middleware.push(routerMiddleware(history));

    const session = createSession({
      ns: 'averpoint',
      selectState(state) {
        if (state.user.user.token) {
          Cookies.set('averpoint.user.token', state.user.user.token, {
            sameSite: 'None',
            secure: true,
            path: '/',
            domain: isProduction ? '.averpoint.com' : undefined,
            expires: 3650,
          });
        } else {
          Cookies.remove('averpoint.user.token', {
            sameSite: 'None',
            secure: true,
            path: '/',
            domain: isProduction ? '.averpoint.com' : undefined,
            expires: 3650,
          });
        }

        return {
          user: state.user,
          // portfolios: state.portfolios,
          // collections: state.collections,
          // notifications: state.notifications
          walkthrough: state.walkthrough,
        };
      },
      onLoad(state, dispatch) {
        /*
         *  Request token from parent window if used as iframe
         */
        if (window.top === window.self) {
          // console.log('in on load of client - session method');
          let token;

          if (state.user && state.user.user && state.user.user.token) {
            // console.log('User on state - going to restore session');
            token = state.user.user.token;
          }

          if (!token) {
            token = Cookies.get('averpoint.user.token');
          }
          Actions.restoreSession(token, dispatch);

          if (token) {
            Actions.getActiveSubscriptions(token, dispatch);
          }

          window.addEventListener('message', (event) => {
            const { data, type } = event.data;

            switch (type) {
              case 'averpoint:request:updateCurrentUser':
                if (
                  state.user.user?.username &&
                  data.user?.username &&
                  state.user.user.username === data.user.username
                ) {
                  dispatch(
                    Actions.userUpdate(
                      mergeUserFromExtension(state.user.user, data.user),
                      false,
                    ),
                  );
                }
                break;
            }
          });
        } else {
          window.addEventListener('message', (event) => {
            const { data, type } = event.data;

            switch (type) {
              case 'averpoint:request:token':
                Actions.restoreSession(data.token, dispatch);
                break;
            }
          });

          window.parent.postMessage({ type: 'averpoint:request:token' }, '*');
        }

        // if (state.walkthrough) {
        //   restoreTutorialState(state.walkthrough, dispatch);
        // }
        // Don't start initializing for a new session on load. Set user reducer to initializing to false at beginning.
        // } else {
        //   userActions.startNewSession(dispatch);
        //   console.log("No user on state - no session to restore");
        // }
      },
    });
    middleware.push(session);
  }

  const reduxEnhancer = [];

  if (isProduction) {
    const sentryReduxEnhancer = Sentry.createReduxEnhancer({
      stateTransformer: (state) => {
        // Do not send any state
        return {};
      },
    });
    reduxEnhancer.push(sentryReduxEnhancer);
  }

  store = createStore(
    // connectRouter(history)(rootReducer(history)),
    rootReducer(history),
    initialState,
    composeWithDevTools(applyMiddleware(...middleware), ...reduxEnhancer),
  );

  if (module.hot) {
    module.hot.accept('./reducers', () => {
      const nextRootReducer = require('./reducers').default;
      store.replaceReducer(nextRootReducer);
    });
  }

  return store;
};
